package co.maytes.standupbot.dashboard.features.standups

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.ControlSize
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideAlertTriangle
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.validation.CronValidator
import co.maytes.standupbot.shared.validation.ValidationResult
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

data class ScheduleSpec(
    val days: Set<Weekday>,
    val hour: Int,
    val minute: Int,
    val timezone: String,
) {
    val firesAtLeastOnce: Boolean get() = days.isNotEmpty()

    fun toCron(): String {
        val daysField =
            if (days.isEmpty()) "?" else Weekday.entries.filter { it in days }
                .joinToString(",") { it.cronToken }
        return "0 $minute $hour ? * $daysField"
    }

    companion object {
        val DEFAULT_DAYS = setOf(
            Weekday.MON, Weekday.TUE, Weekday.WED, Weekday.THU, Weekday.FRI,
        )
        const val DEFAULT_HOUR = 9
        const val DEFAULT_MINUTE = 30

        fun default(timezone: String): ScheduleSpec = ScheduleSpec(
            days = DEFAULT_DAYS,
            hour = DEFAULT_HOUR,
            minute = DEFAULT_MINUTE,
            timezone = timezone,
        )

        fun parse(cron: String, timezone: String): ScheduleSpec {
            val tokens = cron.trim().split(Regex("\\s+"))
            if (tokens.size < CRON_MIN_TOKENS) return default(timezone)
            val minute = tokens[1].toIntOrNull() ?: DEFAULT_MINUTE
            val hour = tokens[2].toIntOrNull() ?: DEFAULT_HOUR
            val dayField = tokens.getOrNull(CRON_DAY_OF_WEEK_INDEX) ?: "?"
            val days =
                if (dayField == "?" || dayField == "*") emptySet()
                else dayField.split(",")
                    .flatMap { Weekday.expand(it.trim()) }
                    .toSet()
            return ScheduleSpec(days = days, hour = hour, minute = minute, timezone = timezone)
        }
    }
}

private const val CRON_MIN_TOKENS = 6
private const val CRON_DAY_OF_WEEK_INDEX = 5

enum class Weekday(val cronToken: String, val label: String) {
    MON("MON", "Mon"),
    TUE("TUE", "Tue"),
    WED("WED", "Wed"),
    THU("THU", "Thu"),
    FRI("FRI", "Fri"),
    SAT("SAT", "Sat"),
    SUN("SUN", "Sun");

    companion object {
        fun fromCronToken(token: String): Weekday? =
            entries.firstOrNull { it.cronToken.equals(token, ignoreCase = true) }

        fun expand(field: String): List<Weekday> {
            val ends = field.split("-")
            val first = fromCronToken(ends.first()) ?: return emptyList()
            if (ends.size == 1) return listOf(first)
            val last = ends.getOrNull(1)?.let(::fromCronToken) ?: return emptyList()
            return generateSequence(first) { if (it == last) null else entries[(it.ordinal + 1) % entries.size] }.toList()
        }
    }
}

@Composable
fun ScheduleBuilder(
    value: ScheduleSpec,
    onChange: (ScheduleSpec) -> Unit,
    timezones: List<String> = SupportedTimezones.list(),
) {
    Card(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(Palette.surfaceSubtle)
            .borderRadius(Radius.md)
            .padding(Spacing.lg),
        padded = false,
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.md)) {
            EyebrowLabel("Days of week")
            DayPillRow(selected = value.days, onToggle = { day ->
                val next = value.days.toMutableSet().apply {
                    if (!add(day)) remove(day)
                }
                onChange(value.copy(days = next))
            })

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .gap(Spacing.lg)
                    .styleModifier { property("flex-wrap", "wrap") },
                verticalAlignment = Alignment.Top,
            ) {
                Column(modifier = Modifier.gap(Spacing.xs)) {
                    EyebrowLabel("Time")
                    TimeInput(
                        hour = value.hour,
                        minute = value.minute,
                        onChange = { h, m -> onChange(value.copy(hour = h, minute = m)) },
                    )
                }
                Column(modifier = Modifier.gap(Spacing.xs)) {
                    EyebrowLabel("Timezone")
                    TimezoneDropdown(
                        value = value.timezone,
                        options = timezones,
                        onChange = { tz -> onChange(value.copy(timezone = tz)) },
                    )
                }
            }

            CronPreviewRow(value)
        }
    }
}

@Composable
private fun EyebrowLabel(text: String) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.caption.size)
            .fontWeight(Typography.caption.weight)
            .color(Palette.textSecondary)
            .styleModifier {
                property("letter-spacing", "0.04em")
                property("text-transform", "none")
            },
    )
}

@Composable
private fun DayPillRow(selected: Set<Weekday>, onToggle: (Weekday) -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .gap(Spacing.xs)
            .styleModifier { property("flex-wrap", "wrap") },
        horizontalArrangement = Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Weekday.entries.forEach { day ->
            val active = day in selected
            Box(
                modifier = Modifier
                    .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
                    .borderRadius(Radius.full)
                    .cursor(Cursor.Pointer)
                    .backgroundColor(if (active) Palette.accent else Palette.surface)
                    .color(if (active) Color.rgb(0xFFFFFF) else Palette.textSecondary)
                    .styleModifier {
                        property(
                            "border",
                            "1px solid ${if (active) Palette.accent.rgbString() else Palette.border.rgbString()}",
                        )
                        property("user-select", "none")
                        property(
                            "transition",
                            Motion.transition(Motion.DUR_BASE, "background-color", "border-color", "color"),
                        )
                    }
                    .onClick { onToggle(day) },
            ) {
                SpanText(
                    text = day.label,
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .fontWeight(500),
                )
            }
        }
    }
}

@Composable
private fun TimeInput(
    hour: Int,
    minute: Int,
    onChange: (Int, Int) -> Unit,
) {
    val display = "${hour.pad2()}:${minute.pad2()}"
    co.maytes.standupbot.dashboard.components.widgets.TimePicker(
        value = display,
        onChange = { raw ->
            val parts = raw.split(":")
            if (parts.size >= 2) {
                val h = parts[0].toIntOrNull() ?: return@TimePicker
                val m = parts[1].toIntOrNull() ?: return@TimePicker
                onChange(h.coerceIn(0, MAX_HOUR), m.coerceIn(0, MAX_MINUTE))
            }
        },
        widthPx = 140,
    )
}

private const val MAX_HOUR = 23
private const val MAX_MINUTE = 59

private fun Int.pad2(): String = toString().padStart(2, '0')

@Composable
private fun TimezoneDropdown(
    value: String,
    options: List<String>,
    onChange: (String) -> Unit,
) {
    val hasCurrent = options.any { it == value }
    val effective = if (!hasCurrent && value.isNotBlank()) listOf(value) + options else options
    Select(
        value = value,
        options = effective.map { SelectOption(it, it) },
        onValueChange = onChange,
        modifier = Modifier
            .styleModifier { property("min-width", "240px") },
        size = ControlSize.Sm,
    )
}

@Composable
private fun CronPreviewRow(value: ScheduleSpec) {
    val cron = value.toCron()
    val validation = CronValidator.validate(cron)
    val fires = value.firesAtLeastOnce
    val ok = validation is ValidationResult.Ok && fires
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xxs)) {
        Row(
            modifier = Modifier.gap(Spacing.xs),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (ok) {
                LucideCheck(size = 14, strokeColor = Palette.success.rgbString())
            } else {
                LucideAlertTriangle(size = 14, strokeColor = Palette.error.rgbString())
            }
            SpanText(
                text = cron,
                modifier = Modifier
                    .fontFamily(*Typography.MONO_STACK)
                    .fontSize(Typography.code.size)
                    .color(if (ok) Palette.textSecondary else Palette.error),
            )
        }
        if (validation is ValidationResult.Error) {
            SpanText(
                text = validation.message,
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.error)
                    .margin(top = Spacing.xxs),
            )
        }
        if (!fires) {
            SpanText(
                text = "Select at least one day — this schedule will never run.",
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.error)
                    .margin(top = Spacing.xxs),
            )
        }
        SpanText(
            text = humanize(value),
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textTertiary),
        )
    }
}

fun humanize(value: ScheduleSpec): String {
    val dayList = if (value.days.isEmpty()) "no days selected" else
        Weekday.entries.filter { it in value.days }.joinToString(", ") { it.label }
    val time = "${value.hour.pad2()}:${value.minute.pad2()}"
    return "$dayList at $time (${value.timezone})"
}

@Suppress("unused")
private fun referencePx() = 1.px

@Suppress("unused")
private fun referencePercent() = 100.percent

internal fun org.jetbrains.compose.web.css.CSSColorValue.rgbString(): String = toString()

object SupportedTimezones {
    private val FALLBACK = listOf(
        "UTC",
        "America/Los_Angeles",
        "America/Denver",
        "America/Chicago",
        "America/New_York",
        "America/Sao_Paulo",
        "Europe/London",
        "Europe/Berlin",
        "Europe/Paris",
        "Europe/Amsterdam",
        "Europe/Madrid",
        "Europe/Istanbul",
        "Asia/Dubai",
        "Asia/Kolkata",
        "Asia/Singapore",
        "Asia/Shanghai",
        "Asia/Tokyo",
        "Asia/Seoul",
        "Australia/Sydney",
        "Pacific/Auckland",
    )

    fun list(): List<String> = runCatching {
        val arr = js("(typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') ? Intl.supportedValuesOf('timeZone') : null")
        @Suppress("UNCHECKED_CAST")
        val asArray = arr as? Array<String>
        asArray?.toList()
    }.getOrNull().orEmpty().ifEmpty { FALLBACK }
}
